import React, { useContext, useEffect, useState } from 'react';
import { Flex, Input, Modal, Radio, Space } from "antd";
import { Switch } from '@headlessui/react'

import { WorkflowCanvasContext } from "../../../../contexts/WorkflowCanvasContextProvider";
import { FaPlus as Plus } from "react-icons/fa6";
import { TiMinus as Minus } from "react-icons/ti";
import { MdDragIndicator } from "react-icons/md";
import { moveDefaultOperatorToEnd } from "../../../../helpers/Utils";
import SettingTip from "../../common/components/setting-tip";
import TextBox from "../../../../components/forms/text-box";
import { Button } from 'flowbite-react';
import { IoIosAdd, IoIosRemove } from "react-icons/io";
import { Dropdown } from "flowbite-react";
import { IoChevronDown } from "react-icons/io5";
import DropDown from "../../../../components/forms/dropdown";
import { CHOICE, CONDITION_OPERATORS } from "../../common/const/settings-type";

// How a branch's condition is authored. Branches saved before natural language
// existed carry no `conditionType`, so the expression form is the fallback everywhere.
const EXPRESSION_CONDITION = "expression";
const NATURAL_CONDITION = "natural";

// The one-line summary shown for a branch in the path list and on the canvas node.
export const describeChoice = (choice, operatorOverride) => {
    if (choice?.conditionType === NATURAL_CONDITION) {
        return choice?.naturalLanguage?.trim() || "Empty Condition";
    }
    if (choice?.expressionList?.length > 0) {
        return choice.expressionList
            .map(expression => `${expression?.variable ? `{${expression?.variable}}` : ""} ${expression?.operator || ""} ${expression?.value || ""}`)
            .join(` ${operatorOverride || choice.operator} `);
    }
    return "Empty Condition";
};

export const customAddRowTheme = {
    "size": {
        "xs": "text-xs px-0 py-0",
    }
};

const ConditionNodeForm = ({ metadata }) => {
    const workflowCanvasContext = useContext(WorkflowCanvasContext);

    const [stateName, setStateName] = useState('');

    const [choiceList, setChoiceList] = useState([]);
    const [selectedChoice, setSelectedChoice] = useState(null);

    const [modalVisible, setModalVisible] = useState(false);

    const [expressionType, setExpressionType] = useState("AND");

    // A branch is authored either as a deterministic rule spec ("expression", the
    // original behaviour and the default for every branch saved before this option
    // existed) or as plain English judged by the LLM at runtime ("natural").
    const [conditionType, setConditionType] = useState(EXPRESSION_CONDITION);
    const [naturalLanguage, setNaturalLanguage] = useState("");
    const [includeConversationHistory, setIncludeConversationHistory] = useState(true);

    const [dragItemId, setDragItemId] = useState(null);
    const [dragOverItemId, setDragOverItemId] = useState(null);

    const handleDragStart = (id) => {
        setDragItemId(id);
    };

    const handleDragOver = (e, id) => {
        e.preventDefault();
        setDragOverItemId(id);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (dragItemId === null || dragOverItemId === null || dragItemId === dragOverItemId) {
            setDragItemId(null);
            setDragOverItemId(null);
            return;
        }

        const nonDefaultItems = choiceList.filter(item => item.operator !== "DEFAULT");
        const defaultItems = choiceList.filter(item => item.operator === "DEFAULT");

        const fromIndex = nonDefaultItems.findIndex(item => item.id === dragItemId);
        const toIndex = nonDefaultItems.findIndex(item => item.id === dragOverItemId);

        if (fromIndex === -1 || toIndex === -1) {
            setDragItemId(null);
            setDragOverItemId(null);
            return;
        }

        const reordered = [...nonDefaultItems];
        const [dragged] = reordered.splice(fromIndex, 1);
        reordered.splice(toIndex, 0, dragged);

        handleUpdateState([...reordered, ...defaultItems]);
        setDragItemId(null);
        setDragOverItemId(null);
        workflowCanvasContext.setPendingNodeInternalsUpdate(workflowCanvasContext.nodeAttribute.parentId);
    };

    const handleDragEnd = () => {
        setDragItemId(null);
        setDragOverItemId(null);
    };

    const { slots } = metadata

    useEffect(() => {
        if (workflowCanvasContext.nodes) {
            const selectedChild = workflowCanvasContext.nodes.find(item => item.id === workflowCanvasContext.nodeAttribute.parentId).data.children.find(item => item.id === workflowCanvasContext.nodeAttribute.id);
            console.log({ selectedChild })
            if (selectedChild?.stateConfig?.choices?.length > 0) {
                setChoiceList(selectedChild?.stateConfig?.choices);
            }
            else {
                setChoiceList([]);
            }
            setStateName(selectedChild?.stateConfig?.name)
        }
    }, [workflowCanvasContext.nodeAttribute.parentId]);

    const handleStateNameChange = event => {
        const value = event.target.value
        const selectedBlock = workflowCanvasContext.nodes.find(item => item.id === workflowCanvasContext.nodeAttribute.parentId)
        selectedBlock.data.children = selectedBlock.data.children.map(item => {

            if (item.id === workflowCanvasContext.nodeAttribute.id) {
                return {
                    ...item,
                    stateConfig: {
                        ...item.stateConfig,
                        name: value
                    }
                }
            }
            return item;
        });

        const findSelectedNodeIndex = workflowCanvasContext.nodes.findIndex(item => item.id === selectedBlock.id);
        workflowCanvasContext.nodes[findSelectedNodeIndex] = selectedBlock;

        setStateName(value);
        workflowCanvasContext.setNodes([...workflowCanvasContext.nodes]);
    }

    const handleNodeAdd = () => {

        const choice = [{
            id: new Date().getMilliseconds(),
            operator: "AND"
        }]

        let _choiceList;
        if (choiceList.length === 0) {
            const elseChoice = {
                id: new Date().getMilliseconds() + Math.random(),
                operator: "DEFAULT",
                active: true,
            }
            choice.push(elseChoice)
            _choiceList = moveDefaultOperatorToEnd([...choiceList, ...choice]);
        } else {
            _choiceList = moveDefaultOperatorToEnd([...choiceList, ...choice]);
        }
        handleUpdateState(_choiceList)


    }

    const handleUpdateState = (_choiceList) => {
        const selectedBlock = workflowCanvasContext.nodes.find(item => item.id === workflowCanvasContext.nodeAttribute.parentId)
        selectedBlock.data.children = selectedBlock.data.children.map(item => {

            if (item.id === workflowCanvasContext.nodeAttribute.id) {
                return {
                    ...item,
                    type: CHOICE,
                    stateConfig: {
                        ...item.stateConfig,
                        choices: [
                            // ...item.stateConfig.choices,
                            // ...choice
                            ..._choiceList
                        ]
                    }
                }
            }
            return item;
        });

        const findSelectedNodeIndex = workflowCanvasContext.nodes.findIndex(item => item.id === selectedBlock.id);
        workflowCanvasContext.nodes[findSelectedNodeIndex] = selectedBlock;

        setChoiceList(_choiceList);
        workflowCanvasContext.setNodes([...workflowCanvasContext.nodes]);
    }

    const deleteChoice = choiceId => {

        const selectedBlock = workflowCanvasContext.nodes
            .find(item => item.id === workflowCanvasContext.nodeAttribute.parentId);
        const updatedChoiceList = choiceList.filter(choice => choice.id !== choiceId)
        setChoiceList(updatedChoiceList.length === 1 ? [] : updatedChoiceList);

        selectedBlock.data.children = selectedBlock.data.children.map(item => {

            if (item.id === workflowCanvasContext.nodeAttribute.id) {
                const updatedList = item.stateConfig.choices
                    .filter(choice => choice.id !== choiceId)
                return {
                    ...item,
                    type: CHOICE,
                    stateConfig: {
                        ...item.stateConfig,
                        choices: updatedList.length === 1 ? [] : updatedList
                    }
                }
            }
            return item;
        });

        const findSelectedNodeIndex = workflowCanvasContext.nodes
            .findIndex(item => item.id === selectedBlock.id);
        workflowCanvasContext.nodes[findSelectedNodeIndex] = selectedBlock;

        workflowCanvasContext.setNodes([...workflowCanvasContext.nodes]);

    }

    const openConditionModal = choice => {
        if (choice?.operator) {
            setExpressionType(choice.operator)
        }

        setConditionType(choice?.conditionType === NATURAL_CONDITION ? NATURAL_CONDITION : EXPRESSION_CONDITION);
        setNaturalLanguage(choice?.naturalLanguage || "");
        // Conversation history is on unless the branch explicitly turned it off, so a
        // branch saved without the flag still gets it.
        setIncludeConversationHistory(choice?.includeConversationHistory !== false);

        setSelectedChoice(choice);
        setModalVisible(true);
    }

    const closeConditionModal = () => {

        const findChoice = choiceList.find(choice => choice.id === selectedChoice.id);

        // Only the fields belonging to the chosen authoring mode are written, so a
        // branch switched from one mode to the other does not keep a stale condition
        // from the mode it left behind.
        const _choice = conditionType === NATURAL_CONDITION
            ? {
                ...findChoice,
                operator: expressionType,
                conditionType: NATURAL_CONDITION,
                naturalLanguage: naturalLanguage,
                includeConversationHistory: includeConversationHistory,
                expressionList: []
            }
            : {
                ...findChoice,
                operator: expressionType,
                conditionType: EXPRESSION_CONDITION,
                naturalLanguage: "",
                expressionList: findChoice?.expressionList || []
            }

        const findChoiceIndex = choiceList.findIndex(choice => choice.id === selectedChoice.id);

        choiceList[findChoiceIndex] = _choice;

        const selectedBlock = workflowCanvasContext.nodes
            .find(item => item.id === workflowCanvasContext.nodeAttribute.parentId);

        selectedBlock.data.children = selectedBlock.data.children.map(item => {

            if (item.id === workflowCanvasContext.nodeAttribute.id) {
                return {
                    ...item,
                    stateConfig: {
                        ...item.stateConfig,
                        choices: choiceList,
                        stateType: CHOICE
                    }
                }
            }
            return item;
        });

        const findSelectedNodeIndex = workflowCanvasContext.nodes
            .findIndex(item => item.id === selectedBlock.id);
        workflowCanvasContext.nodes[findSelectedNodeIndex] = selectedBlock;

        workflowCanvasContext.setNodes([...workflowCanvasContext.nodes]);

        setSelectedChoice(null);
        setExpressionType("AND")
        setConditionType(EXPRESSION_CONDITION);
        setNaturalLanguage("");
        setIncludeConversationHistory(true);
        setModalVisible(false);
    }

    const addExpression = () => {

        const _expressionList = selectedChoice?.expressionList ? selectedChoice.expressionList : [];

        const _selectedChoice = {
            ...selectedChoice,
            expressionList: [
                ..._expressionList,
                {
                    id: new Date().getMilliseconds(),
                    operator: "is",
                }

            ]
        }

        setSelectedChoice(_selectedChoice);

    }

    const handleConditionChange = (expressionId, e) => {

        const { name, value } = e.target;

        selectedChoice.expressionList = selectedChoice?.expressionList?.map(expression => {
            if (expression.id === expressionId) {
                return {
                    ...expression,
                    [name]: value
                }
            }
            return expression
        })


        const findChoiceListIndex = choiceList
            .findIndex(choice => choice.id === selectedChoice.id);

        choiceList[findChoiceListIndex] = selectedChoice;

        setChoiceList([...choiceList]);

    }

    const deleteExpression = expressionId => {

        const _filteredSelectedExpressionList = selectedChoice?.expressionList?.filter(expression => expression.id !== expressionId);

        const _selectedChoice = {
            ...selectedChoice,
            expressionList: _filteredSelectedExpressionList
        }

        const findChoiceIndex = choiceList.findIndex(choice => choice.id === selectedChoice.id);

        choiceList[findChoiceIndex] = _selectedChoice;

        setSelectedChoice(_selectedChoice);
        setChoiceList([...choiceList]);


    }

    const handleElse = (checked) => {
        if (checked) {
            // choiceList
            const updatedChoiceList = choiceList.map(choice => {
                if (choice.operator === "DEFAULT") {
                    choice.active = true;
                    return choice;
                }
                return choice;
            });
            handleUpdateState(updatedChoiceList);
        }
        else {
            const updatedChoiceList = choiceList.map(choice => {
                if (choice.operator === "DEFAULT") {
                    choice.active = false;
                    return choice;
                }
                return choice;
            });
            handleUpdateState(updatedChoiceList);
        }
    }

    return (
        <div className="flex flex-col gap-2">
            <SettingTip text="Add conditional path for your workflow" />
            {/*<TextBox*/}
            {/*    label="Name"*/}
            {/*    type="text"*/}
            {/*    name="name"*/}
            {/*    placeholder="State name"*/}
            {/*    value={stateName}*/}
            {/*    onChange={handleStateNameChange}*/}
            {/*/>*/}

            <div className="flex flex-row items-center justify-between">
                <label className="block text-sm font-medium text-gray-900">
                    Paths
                </label>
                <Button size="xs" theme={customAddRowTheme} color="gray" onClick={handleNodeAdd}>
                    <IoIosAdd className="h-6 w-6" />
                </Button>
            </div>

            <div className="pt-1 flex flex-col gap-2">
                {
                    choiceList.map(item => (
                        <div
                            key={item.id}
                            draggable={item.operator !== "DEFAULT"}
                            onDragStart={() => handleDragStart(item.id)}
                            onDragOver={(e) => item.operator !== "DEFAULT" && handleDragOver(e, item.id)}
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}
                            className={item.operator !== "DEFAULT" && dragOverItemId === item.id ? "opacity-50" : ""}
                        >
                            {item.operator !== "DEFAULT" ?
                                <div className="flex flex-row gap-2 justify-between w-full">
                                    <MdDragIndicator className="text-gray-400 cursor-grab self-center shrink-0" />
                                    <div
                                        className={`rounded-lg flex flex-row cursor-pointer items-center hover:px-2 active:px-2 w-full block text-sm font-medium text-gray-500 hover:bg-blue-500 hover:text-fixed-white ${selectedChoice?.id === item.id ? 'bg-blue-500 text-fixed-white' : ''}`}
                                        onClick={() => openConditionModal(item)}
                                    >
                                        {describeChoice(
                                            item,
                                            modalVisible && selectedChoice?.id === item.id ? expressionType : item.operator
                                        )}
                                    </div>
                                    <Button size="xs" theme={customAddRowTheme} color="gray" onClick={() => deleteChoice(item.id)}>
                                        <IoIosRemove className="h-6 w-6" />
                                    </Button>
                                </div> :
                                <div className="pt-4 flex flex-row justify-between items-center">
                                    <label htmlFor="first-name" className="block text-sm font-medium text-gray-700">
                                        Else
                                    </label>
                                    <Switch
                                        checked={item?.active}
                                        onChange={handleElse}
                                        className={`${item?.active ? 'bg-blue-600' : 'bg-gray-200'
                                            } relative inline-flex h-6 w-11 items-center rounded-full`}
                                    >
                                        <span className="sr-only">Send to User</span>
                                        <span
                                            className={`${item?.active ? 'translate-x-6' : 'translate-x-1'
                                                } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                                        />
                                    </Switch>
                                </div>}
                        </div>
                    ))
                }
            </div>

            <Modal
                title={
                    <div className="flex flex-row items-start justify-between gap-4 pr-2">
                        <div className="min-h-[32px] flex items-center">
                            {conditionType === EXPRESSION_CONDITION &&
                                <Space size="large">
                                    <Radio.Group
                                        value={expressionType}
                                        buttonStyle="solid"
                                        onChange={e => setExpressionType(e.target.value)}
                                    >
                                        <Radio.Button value="AND">
                                            Match All
                                        </Radio.Button>
                                        <Radio.Button value="OR">
                                            Match Any
                                        </Radio.Button>
                                    </Radio.Group>

                                    <Button size="xs" color="gray" onClick={addExpression}>
                                        <IoIosAdd className="h-6 w-6" />
                                    </Button>
                                </Space>
                            }
                        </div>

                        <div className="flex flex-row items-center gap-2 shrink-0">
                            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                Natural Language
                            </label>
                            <Switch
                                checked={conditionType === NATURAL_CONDITION}
                                onChange={checked => setConditionType(checked ? NATURAL_CONDITION : EXPRESSION_CONDITION)}
                                className={`${conditionType === NATURAL_CONDITION ? 'bg-blue-600' : 'bg-gray-200'
                                    } relative inline-flex h-6 w-11 items-center rounded-full shrink-0`}
                            >
                                <span className="sr-only">Describe this path in natural language</span>
                                <span
                                    className={`${conditionType === NATURAL_CONDITION ? 'translate-x-6' : 'translate-x-1'
                                        } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                                />
                            </Switch>
                        </div>
                    </div>
                }
                closeIcon={null}
                open={modalVisible}
                onCancel={closeConditionModal}
                footer={null}
            >
                <div>
                    {conditionType === NATURAL_CONDITION &&
                        <div className="flex flex-col gap-3">
                            <label className="block text-sm font-medium text-gray-900">
                                Describe when this path should be taken
                            </label>
                            <Input.TextArea
                                rows={4}
                                value={naturalLanguage}
                                placeholder="e.g. if {order_status} is still processing also user enable the notification"
                                onChange={e => setNaturalLanguage(e.target.value)}
                            />
                            <p className="text-xs text-gray-500">
                                Evaluated by AI at runtime using the current slot values. Reference a slot
                                with {"{slot_name}"}. Available slots:{" "}
                                {Object.keys(metadata.slots || {}).sort((a, b) => a.localeCompare(b)).map(slot => `{${slot}}`).join(", ") || "none"}
                            </p>

                            <div className="pt-2 flex flex-row justify-between items-center">
                                <div className="flex flex-col">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Include conversation history
                                    </label>
                                    <span className="text-xs text-gray-500">
                                        Give the AI the recent conversation as extra context.
                                    </span>
                                </div>
                                <Switch
                                    checked={includeConversationHistory}
                                    onChange={setIncludeConversationHistory}
                                    className={`${includeConversationHistory ? 'bg-blue-600' : 'bg-gray-200'
                                        } relative inline-flex h-6 w-11 items-center rounded-full shrink-0`}
                                >
                                    <span className="sr-only">Include conversation history</span>
                                    <span
                                        className={`${includeConversationHistory ? 'translate-x-6' : 'translate-x-1'
                                            } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                                    />
                                </Switch>
                            </div>
                        </div>
                    }

                    {
                        conditionType === EXPRESSION_CONDITION && selectedChoice && selectedChoice?.expressionList?.map(expression =>
                            <div className="flex flex-row gap-4 items-center" key={expression.id}>
                                <p className="text-gray-900 font-semibold">if</p>
                                <div className="grid grid-cols-3 gap-4 gap-1 items-center">
                                    <DropDown
                                        className=""
                                        placeHolder="variable"
                                        name="variable"
                                        value={expression.variable}
                                        options={[...Object.keys(metadata.slots).sort((a, b) => a.localeCompare(b)), "{sys_input_text}", "{sys_channel}"]}
                                        onChange={e => handleConditionChange(expression.id, e)}
                                        inline={false}
                                    />
                                    <DropDown
                                        className=""
                                        placeHolder="operator"
                                        name="operator"
                                        value={expression?.operator}
                                        options={CONDITION_OPERATORS}
                                        onChange={e => handleConditionChange(expression.id, e)}
                                        inline={false}
                                    />
                                    {/*<Switch*/}
                                    {/*    style={{width: "250px"}}*/}
                                    {/*    checkedChildren="is"*/}
                                    {/*    unCheckedChildren="is not"*/}
                                    {/*    checked={expression?.operator === "is"}*/}
                                    {/*    onChange={v => handleConditionChange(expression.id, {*/}
                                    {/*        target: {name: "operator", value: v ? "is" : "is not"}*/}
                                    {/*    })*/}
                                    {/*    }*/}
                                    {/*/>*/}
                                    <TextBox
                                        rootClass=""
                                        type="text"
                                        name="value"
                                        placeholder="value or {var}"
                                        value={expression.value}
                                        onChange={e => handleConditionChange(expression.id, e)}
                                    />
                                </div>


                                <Button size="xs" theme={customAddRowTheme} color="gray" onClick={() => deleteExpression(expression.id)}>
                                    <Minus />
                                </Button>
                            </div>
                        )
                    }

                </div>
            </Modal>

        </div>
    );
};

export default ConditionNodeForm;
